// Route-history maths — pure helpers behind the route drawer: the timeline
// (day dividers, points, gaps), the gap rule, the preset date ranges and the
// custom range's ISO edges. `t` comes in as a parameter so tests assert the
// interpolation args.

import {
  RouteDateFilter,
  type LiveLocation,
  type UserRoute,
} from '@/api/endpoints/liveTrackingApi';

type T = (key: string, opts?: Record<string, unknown>) => string;

export type TimelineEntry =
  /** The local day changed — a divider stamped with the first point's instant. */
  | { kind: 'day'; at: string }
  | { kind: 'point'; point: LiveLocation; isLatest: boolean }
  /** No fix for longer than the threshold between two points of ONE clock. */
  | { kind: 'gap'; from: LiveLocation; to: LiveLocation; ms: number };

/** A silence counts as a gap once it exceeds twice the capture interval
 *  (user decision): 15 min → anything over 30 min. */
export const gapThresholdMs = (periodMinutes: number): number => periodMinutes * 2 * 60_000;

const sameLocalDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/** Flattens the routes (oldest clock first) into what the list and the map
 *  render. Gaps are only ever computed WITHIN a clock — between two clocks
 *  the person was off shift, which is not a missing location. */
export const buildTimeline = (routes: UserRoute[], thresholdMs: number): TimelineEntry[] => {
  const entries: TimelineEntry[] = [];
  const lastRoute = routes[routes.length - 1];
  const latest = lastRoute?.points[lastRoute.points.length - 1];
  let lastDay: Date | null = null;

  routes.forEach((route) => {
    route.points.forEach((point, i) => {
      const at = new Date(point.trackedAt);
      if (!lastDay || !sameLocalDay(lastDay, at)) {
        entries.push({ kind: 'day', at: point.trackedAt });
        lastDay = at;
      }
      if (i > 0) {
        const prev = route.points[i - 1];
        const ms = at.getTime() - new Date(prev.trackedAt).getTime();
        if (ms > thresholdMs) entries.push({ kind: 'gap', from: prev, to: point, ms });
      }
      entries.push({ kind: 'point', point, isLatest: point === latest });
    });
  });
  return entries;
};

export const countPoints = (routes: UserRoute[]): number =>
  routes.reduce((n, r) => n + r.points.length, 0);

export const countGaps = (entries: TimelineEntry[]): number =>
  entries.filter((e) => e.kind === 'gap').length;

/** "2 h 6 min" / "45 min" through the i18n keys. */
export const fmtGapDuration = (ms: number, t: T): string => {
  const mins = Math.max(0, Math.round(ms / 60_000));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0
    ? t('attendance_route_duration_hm', { h, m })
    : t('attendance_route_duration_m', { m });
};

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const endOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
const daysBefore = (d: Date, days: number) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate() - days);

/** The local window a preset covers (null for Custom — the inputs decide). */
export const presetRange = (
  preset: RouteDateFilter,
  now: Date,
): { from: Date; to: Date } | null => {
  switch (preset) {
    case RouteDateFilter.Today:
      return { from: startOfDay(now), to: now };
    case RouteDateFilter.Yesterday: {
      const y = daysBefore(now, 1);
      return { from: startOfDay(y), to: endOfDay(y) };
    }
    case RouteDateFilter.Last7Days:
      return { from: daysBefore(now, 6), to: now };
    case RouteDateFilter.Last30Days:
      return { from: daysBefore(now, 29), to: now };
    default:
      return null;
  }
};

/** "Aug 16, 2026" for a single day, "Aug 10 – 16, 2026" for a span. */
export const fmtPresetRange = (preset: RouteDateFilter, now: Date, locale: string): string => {
  const range = presetRange(preset, now);
  if (!range) return '';
  const fmt = new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', year: 'numeric' });
  return sameLocalDay(range.from, range.to)
    ? fmt.format(range.from)
    : fmt.formatRange(range.from, range.to);
};

/** The custom range's wire edges: the From day's local start and the To
 *  day's local end as ISO (inclusive window, the ActivityLog idiom). Null
 *  while either date is missing / unparseable or the order is wrong. */
export const customRangeIso = (
  fromYmd: string,
  toYmd: string,
): { startDate: string; endDate: string } | null => {
  if (!fromYmd || !toYmd) return null;
  const start = new Date(`${fromYmd}T00:00:00.000`);
  const end = new Date(`${toYmd}T23:59:59.999`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return null;
  return { startDate: start.toISOString(), endDate: end.toISOString() };
};
